import React, { Component, ErrorInfo, ReactNode } from "react";
import { Flame, RefreshCw, AlertTriangle, Copy, Check, RotateCcw } from "lucide-react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  copied: boolean;
}

export class AppErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      copied: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("[AppErrorBoundary] Intercepted unhandled React render error:", error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleResetCache = () => {
    try {
      localStorage.removeItem("vedic_mantras_store");
      localStorage.removeItem("vedic_settings");
      localStorage.removeItem("vedic_history");
      sessionStorage.clear();
    } catch (e) {
      console.warn("Storage reset exception:", e);
    }
    window.location.reload();
  };

  handleCopyDiagnostics = () => {
    const text = `Error: ${this.state.error?.message || "Unknown error"}\n\nStack:\n${this.state.error?.stack || ""}\n\nComponent Stack:\n${this.state.errorInfo?.componentStack || ""}`;
    navigator.clipboard.writeText(text).then(() => {
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2500);
    }).catch(() => {});
  };

  override render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center p-4">
          <div className="w-full max-w-xl bg-slate-900 border border-amber-500/30 rounded-2xl p-6 sm:p-8 shadow-2xl relative overflow-hidden">
            {/* Subtle sacred ambient glow */}
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />

            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl text-amber-400">
                <Flame className="w-6 h-6 animate-pulse" />
              </div>
              <div>
                <h1 className="text-xl font-bold tracking-tight text-amber-200">
                  Ancient Runes Resilience Shield
                </h1>
                <p className="text-xs text-slate-400">
                  BhutaDamaraSena R&D Labs · Acoustic Matrix Protected
                </p>
              </div>
            </div>

            <div className="p-4 bg-slate-950/80 border border-slate-800 rounded-xl mb-6">
              <div className="flex items-start gap-2.5 text-xs text-amber-300/90 mb-2 font-medium">
                <AlertTriangle className="w-4 h-4 shrink-0 text-amber-400 mt-0.5" />
                <span>
                  A runtime component exception was safely intercepted to prevent full window termination.
                </span>
              </div>
              <div className="text-xs font-mono text-slate-300 break-words bg-black/40 p-2.5 rounded border border-white/5 max-h-32 overflow-y-auto">
                {this.state.error?.message || "Render exception encountered."}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <button
                onClick={this.handleReload}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-semibold text-sm rounded-xl transition-all shadow-lg active:scale-98 cursor-pointer"
              >
                <RefreshCw className="w-4 h-4" />
                Reload Ritual Matrix
              </button>

              <button
                onClick={this.handleResetCache}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-200 font-medium text-xs rounded-xl border border-slate-700 transition-all cursor-pointer"
              >
                <RotateCcw className="w-4 h-4" />
                Clear Local Cache & Reset
              </button>

              <button
                onClick={this.handleCopyDiagnostics}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 bg-slate-800/60 hover:bg-slate-800 text-slate-300 text-xs rounded-xl border border-slate-700/60 transition-all cursor-pointer"
                title="Copy error diagnostics"
              >
                {this.state.copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                <span>{this.state.copied ? "Copied" : "Diagnostics"}</span>
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
